import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Modal from '@cloudscape-design/components/modal';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import { suspendTenantCallable } from '@/lib/callables';
import { ALL_SUSPENSION_REASONS, SuspensionReason, type TenantId } from '@etiquetachef/types';

type Props = {
  open: boolean;
  tenantId: TenantId;
  tenantName: string;
  onClose: () => void;
};

/**
 * Suspende um tenant manualmente (`suspendTenant`), com motivo obrigatório.
 *
 * Origem: sem issue — commit efa5a8ca2, 2026-04-26 17:22 +0200 (Sprint 2).
 *
 * Contrato: o operador escolhe o TIPO (`reasonCode`, `SuspensionReason`,
 * padrão `MANUAL`) e escreve o motivo livre (obrigatório, vai ao audit).
 * Motivo vazio → erro local sem chamar; senão
 * `suspendTenantCallable({ tenantId, reasonCode, reason })`, fecha no
 * sucesso, `err.message` em `Alert` dentro do modal no erro (#1021). O servidor grava
 * `tenants/{id}.status` (`SUSPENDED_BILLING` para `BILLING_OVERDUE`, senão
 * `SUSPENDED_ADMIN`), `billing/subscription.status = SUSPENDED` e audit;
 * idempotente se já suspenso (`apps/functions/src/admin/suspendTenant.ts`).
 *
 * Falhas conhecidas:
 *  - Até 2026-10-13 (#935, item 5): o diálogo mandava só o texto livre como
 *    `reason`; `SUSPENDED_BILLING` só saía se o operador digitasse exatamente
 *    `BILLING_OVERDUE` — na prática toda suspensão manual saía
 *    `SUSPENDED_ADMIN`.
 *
 * Permissão no
 * servidor: `requireTenantOperator` com `staffOnly` e
 * `staffRoles: ADMIN_PERMISSIONS.suspendTenant` (ROOT/OPS —
 * `docs/permissions.md`); usuário do CRM não suspende desde a #776.
 *
 * Usado por: `BillingActions`, montado em `routes/TenantDetailPage.tsx`
 * (rota `/tenants/:id`, aberta pela lista do menu "Tenants"), admin web.
 * Spec: `docs/screens/tenant-detail-admin.md`.
 *
 * Issues: #776 (ação só do staff), #795 (ADM-7, capability ROOT/OPS).
 *
 * Histórico:
 *  - 2026-06-08 21:47 +0200 — Cloudscape (39-E).
 *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
 *  - 2026-10-13 10:50 +0200 — (#935, item 5) seletor do tipo (`reasonCode`).
 *  - 2026-10-25 14:29 +0100 — (#1021) erro da ação dentro do modal (o toast ficava atrás do overlay).
 */
export function SuspendDialog({ open, tenantId, tenantName, onClose }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const [reasonCode, setReasonCode] = useState<SuspensionReason>(SuspensionReason.MANUAL);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Limpa motivo e erro e devolve o controle ao pai. */
  function close(): void {
    setReasonCode(SuspensionReason.MANUAL);
    setReason('');
    setError(null);
    onClose();
  }

  /** Valida o motivo e chama `suspendTenant`; erro vira `Alert` no modal (#1021). */
  async function handleSubmit(): Promise<void> {
    if (reason.trim().length === 0) {
      setError(t('tenantDetail.actions.reasonRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Bug corrigido em 2026-10-13 10:50 +0200 (#935, item 5): mandava só o
      // texto livre, e o servidor tirava o código dele — "Cobrança em atraso"
      // nunca virava SUSPENDED_BILLING. O código agora é explícito.
      await suspendTenantCallable({ tenantId, reasonCode, reason: reason.trim() });
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={open}
      // #1021 (2ª auditoria 2026-10-25 15:52 +0100): Esc/X/clique fora fechavam o modal com a chamada
      // em voo, e a falha caía no `Alert` de um modal já fechado — ninguém a via (o Cancelar já
      // ficava desligado; o `onDismiss` não).
      onDismiss={(): void => {
        if (!submitting) close();
      }}
      header={t('tenantDetail.actions.suspendTitle')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close} disabled={submitting}>
              {t('tenantDetail.actions.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={(): void => {
                void handleSubmit();
              }}
              loading={submitting}
            >
              {t('tenantDetail.actions.confirmSuspend')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">
          {t('tenantDetail.actions.suspendDescription', { name: tenantName })}
        </Box>
        <FormField
          label={t('tenantDetail.actions.suspendReasonCodeLabel')}
          description={t('tenantDetail.actions.suspendReasonCodeHint')}
        >
          <Select
            selectedOption={{ value: reasonCode, label: t(`tenantDetail.actions.suspendReasonCode.${reasonCode}`) }}
            options={ALL_SUSPENSION_REASONS.map((code) => ({
              value: code,
              label: t(`tenantDetail.actions.suspendReasonCode.${code}`),
            }))}
            onChange={({ detail }): void => {
              const next = ALL_SUSPENSION_REASONS.find((code) => code === detail.selectedOption.value);
              if (next) setReasonCode(next);
            }}
            disabled={submitting}
          />
        </FormField>
        <FormField label={t('tenantDetail.actions.reasonLabel')}>
          <Textarea
            value={reason}
            onChange={({ detail }): void => {
              setReason(detail.value);
              // #1021 (2026-10-25 15:00 +0100): digitar o motivo tira o "motivo obrigatório" do Alert.
              setError(null);
            }}
            rows={3}
            disabled={submitting}
            placeholder={t('tenantDetail.actions.reasonPlaceholder')}
          />
        </FormField>
        {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do overlay
            do Modal (mesmo achado do CRM, #174). Fica dentro do modal, junto do footer. */}
        {error !== null && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
